import React, { useState, useEffect } from 'react';

const Envelopes = ({ product, updateProduct }) => {
  const [selectedMaterial, setSelectedMaterial] = useState(product.fields.material || '');
  const [width, setWidth] = useState(product.fields.width || '');
  const [height, setHeight] = useState(product.fields.height || '');
  const [customMaterial, setCustomMaterial] = useState(product.fields.customMaterial || '');

  useEffect(() => {
    updateProduct('width', width);
  }, [width, updateProduct]);

  useEffect(() => {
    updateProduct('height', height);
  }, [height, updateProduct]);

  // Paper-based material options for envelopes
  const materialOptions = [
    "White Cardboard",
    "Kraft Paper",
    "Corrugated Paper",
    "Hologram Cardboard",
    "CCNB (Clay Coated New Back)",
    "SBS (Solid Bleached Sulfate)",
    "Other"
  ];

  // Material options mapping for envelopes
  const materialToOptionsMap = {
    "White Cardboard": {
      thickness: ["200g", "250g", "300g", "350g", "400g"],
      printingFinishes: ["Gloss Lamination", "Matte Lamination", "Gloss Varnish", "Matte Varnish", "Soft Touch", "Hologram Lamination"],
      specialFeatures: ["Light Proof", "Biodegradable", "Recyclable", "Clear Window", "Sturdy", "Reinforced"]
    },
    "Kraft Paper": {
      thickness: ["200g", "250g", "300g", "350g"],
      printingFinishes: ["Gloss Lamination", "Matte Lamination", "Gloss Varnish", "Matte Varnish"],
      specialFeatures: ["Light Proof", "Biodegradable", "Recyclable", "Clear Window", "Sturdy", "Reinforced"]
    },
    "Corrugated Paper": {
      thickness: ["250g/300g/350g White Cardboard + Corrugated Paper"],
      printingFinishes: ["Gloss Lamination", "Matte Lamination", "Gloss Varnish", "Matte Varnish", "Soft Touch", "Hologram Lamination"],
      specialFeatures: ["Light Proof", "Biodegradable", "Recyclable", "Clear Window", "Sturdy", "Reinforced"]
    },
    "Hologram Cardboard": {
      thickness: ["?"],
      printingFinishes: [],
      specialFeatures: ["Light Proof", "Biodegradable", "Recyclable", "Clear Window", "Sturdy", "Reinforced"]
    },
    "CCNB (Clay Coated New Back)": {
      thickness: ["200g", "250g", "300g", "350g", "400g"],
      printingFinishes: ["Gloss Lamination", "Matte Lamination", "Gloss Varnish", "Matte Varnish", "Soft Touch", "Hologram Lamination"],
      specialFeatures: ["Light Proof", "Biodegradable", "Recyclable", "Clear Window", "Sturdy", "Reinforced"]
    },
    "SBS (Solid Bleached Sulfate)": {
      thickness: ["200g", "250g", "300g", "350g", "400g"],
      printingFinishes: ["Gloss Lamination", "Matte Lamination", "Gloss Varnish", "Matte Varnish", "Soft Touch", "Hologram Lamination"],
      specialFeatures: ["Light Proof", "Biodegradable", "Recyclable", "Clear Window", "Sturdy", "Reinforced"]
    }
  };

  // Handle material change
  const handleMaterialChange = (e) => {
    const material = e.target.value;
    setSelectedMaterial(material);
    
    if (material === 'Other') {
      updateProduct('material', '');
    } else {
      updateProduct('material', material);
      setCustomMaterial('');
    }
    
    // Reset dependent fields
    updateProduct('thickness', '');
    updateProduct('printingFinishes', '');
    updateProduct('specialFeatures', []);
  };

  // Add handler for custom material input
  const handleCustomMaterialChange = (e) => {
    const value = e.target.value;
    setCustomMaterial(value);
    updateProduct('material', value);
    updateProduct('customMaterial', value);
  };

  // Render conditional fields based on selected material
  const renderConditionalFields = () => {
    if (!selectedMaterial) return null;
    
    const options = materialToOptionsMap[selectedMaterial] || {
      thickness: [],
      printingFinishes: [],
      specialFeatures: []
    };

    return (
      <>
        {/* Thickness */}
        <div className="form-group">
          <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Thickness (g):</label>
          <input
            type="number"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg"
            value={product.fields.thickness || '350'} 
            min={1}
            onChange={(e) => updateProduct('thickness', e.target.value)}
          />
          <div className="mt-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="mr-2"
                checked={product.fields.requestThicknessRecommendation || false}
                onChange={(e) => updateProduct('requestThicknessRecommendation', e.target.checked)}
              />
              <span className="text-sm">Request a recommendation from production</span>
            </label>
          </div>
        </div>

        {/* Printing Finishes */}
        {Array.isArray(options.printingFinishes) && options.printingFinishes.length > 0 && (
          <div className="form-group">
            <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Printing Finishes:</label>
            <select
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg"
              value={product.fields.printingFinishes || ''}
              onChange={(e) => updateProduct('printingFinishes', e.target.value)}
            >
              <option value="">Select Printing Finish</option>
              {options.printingFinishes.map((finish, index) => (
                <option key={index} value={finish}>{finish}</option>
              ))}
            </select>
          </div>
        )}

        {/* Special Features */}
        <div className="form-group">
          <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Special Features:</label>
          <div className="grid gap-2 grid-cols-2">
            {options.specialFeatures.map((feature, index) => (
              <label key={index} className="flex items-center">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={product.fields.specialFeatures?.includes(feature) || false}
                  onChange={(e) => {
                    let currentFeatures = product.fields.specialFeatures ? product.fields.specialFeatures.split(',') : [];
                    if (currentFeatures.includes(feature)) {
                      currentFeatures = currentFeatures.filter(f => f !== feature);
                    } else {
                      currentFeatures.push(feature);
                    }
                    updateProduct('specialFeatures', currentFeatures.join(','));
                  }}
                />
                {feature}
              </label>
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="product-form">
      {/* Size (W x H) */}
      <div className="form-group">
        <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Size (W x H in mm):</label>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Width (W)</label>
            <input
              type="number"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              placeholder="Enter width"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Height (H)</label>
            <input
              type="number"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder="Enter height"
              required
            />
          </div>
        </div>
      </div>

      {/* Material */}
      <div className="form-group">
        <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Primary Material:</label>
        <select
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5"
          value={selectedMaterial}
          onChange={handleMaterialChange}
        >
          <option value="">Select Primary Material</option>
          {materialOptions.map((material, index) => (
            <option key={index} value={material}>{material}</option>
          ))}
        </select>
        
        {selectedMaterial === 'Other' && (
          <div className="mt-2">
            <input
              type="text"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5"
              value={customMaterial}
              onChange={handleCustomMaterialChange}
              placeholder="Enter custom material"
            />
          </div>
        )}
      </div>

      {/* Secondary Material */}
      <div className="form-group">
        <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Secondary Material:</label>
        <input
          type="text"
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5"
          value={product.fields.secondaryMaterial || ''}
          onChange={(e) => updateProduct('secondaryMaterial', e.target.value)}
          placeholder="Enter secondary material"
        />
      </div>

      {/* Conditional Fields Based on Material */}
      {renderConditionalFields()}

      {/* Product Type */}
      <div className="form-group">
        <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">What type of products will be used in the envelopes?</label>
        <input
          type="text"
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
          value={product.fields.productType || ''}
          onChange={(e) => updateProduct('productType', e.target.value)}
          placeholder="Enter product type"
        />
      </div>
    </div>
  );
};

export default Envelopes;